import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Composable for managing carousel functionality
 * @param {number} totalSlides - Total number of slides in the carousel
 * @param {number} autoPlayInterval - Auto-play interval in milliseconds (default: 5000)
 * @returns {Object} Carousel state and controls
 */
export function useCarousel(totalSlides, autoPlayInterval = 5000) {
    const currentSlide = ref(0)
    const isPaused = ref(false)
    let intervalId = null

    /**
     * Navigate to the next slide
     */
    const nextSlide = () => {
        currentSlide.value = (currentSlide.value + 1) % totalSlides
    }

    /**
     * Navigate to the previous slide
     */
    const prevSlide = () => {
        currentSlide.value = currentSlide.value === 0
            ? totalSlides - 1
            : currentSlide.value - 1
    }

    /**
     * Navigate to a specific slide
     * @param {number} index - Slide index to navigate to
     */
    const goToSlide = (index) => {
        if (index >= 0 && index < totalSlides) {
            currentSlide.value = index
        }
    }

    /**
     * Start auto-play
     */
    const startAutoPlay = () => {
        if (intervalId) return

        intervalId = setInterval(() => {
            if (!isPaused.value) {
                nextSlide()
            }
        }, autoPlayInterval)
    }

    /**
     * Stop auto-play
     */
    const stopAutoPlay = () => {
        if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
        }
    }

    /**
     * Pause auto-play (temporarily)
     */
    const pause = () => {
        isPaused.value = true
    }

    /**
     * Resume auto-play
     */
    const resume = () => {
        isPaused.value = false
    }

    // Lifecycle hooks
    onMounted(() => {
        startAutoPlay()
    })

    onUnmounted(() => {
        stopAutoPlay()
    })

    return {
        currentSlide,
        isPaused,
        nextSlide,
        prevSlide,
        goToSlide,
        pause,
        resume,
        startAutoPlay,
        stopAutoPlay
    }
}
